import { PDFDocument } from 'pdf-lib';

interface WatermarkOptions {
	text: string;
	opacity?: number;
	fontSize?: number;
	color?: string;
}

const DEFAULT_WATERMARK_OPTIONS = {
	text: '',
	opacity: 0.3,
	fontSize: 48,
	color: 'black',
} as const;

/**
 * Adds a watermark to an image by creating a canvas overlay
 * @param imageUrl The URL of the image to watermark
 * @param options Watermark options including text and styling
 * @returns A Promise that resolves to a Blob containing the watermarked image
 */
export async function addImageWatermark(
	imageUrl: string,
	options: WatermarkOptions,
): Promise<Blob> {
	const {
		text,
		opacity = DEFAULT_WATERMARK_OPTIONS.opacity!,
		color = DEFAULT_WATERMARK_OPTIONS.color!,
	} = options;

	const img = new Image();
	img.crossOrigin = 'anonymous';

	await new Promise((resolve, reject) => {
		img.onload = resolve;
		img.onerror = reject;
		img.src = imageUrl;
	});

	const canvas = document.createElement('canvas');
	canvas.width = img.width;
	canvas.height = img.height;
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('Could not get canvas context');

	ctx.drawImage(img, 0, 0);

	const lines = text.split('\n');
	const fontFamily = 'Arial';
	let fontSize = Math.min(img.width, img.height) * 0.5;

	const tempCanvas = document.createElement('canvas');
	const tempCtx = tempCanvas.getContext('2d');
	if (!tempCtx) throw new Error('Could not get temp canvas context');

	// Find the maximum font size that fits 50% of the image's width and height, considering rotation
	while (fontSize > 1) {
		tempCtx.font = `${fontSize}px ${fontFamily}`;
		const textWidth = Math.max(
			...lines.map((line) => tempCtx.measureText(line).width),
		);
		const textHeight = fontSize * lines.length * 1.2;
		const rotatedBox = Math.sqrt(
			textWidth * textWidth + textHeight * textHeight,
		);

		if (rotatedBox <= canvas.width * 0.5 && rotatedBox <= canvas.height * 0.5) {
			break;
		}
		fontSize--;
	}

	ctx.save();
	ctx.globalAlpha = opacity;
	ctx.font = `${fontSize}px ${fontFamily}`;
	ctx.fillStyle = color;
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.translate(canvas.width / 2, canvas.height / 2);
	ctx.rotate(-Math.PI / 4);

	const lineHeight = fontSize * 1.2;
	const totalHeight = lineHeight * lines.length;
	const startY = -(totalHeight / 2) + lineHeight / 2;

	lines.forEach((line, index) => {
		ctx.fillText(line, 0, startY + index * lineHeight);
	});

	ctx.restore();

	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (blob) resolve(blob);
				else reject(new Error('Failed to create blob from canvas'));
			},
			'image/jpeg',
			0.95,
		);
	});
}

/**
 * Creates a canvas with watermark text and returns it as a blob
 * The font size is dynamically calculated so the widest line is 50% of the PDF width
 */
async function createWatermarkCanvas(
	text: string,
	options: { width: number; height: number; color: string; opacity: number },
): Promise<Blob> {
	const { width, height, color, opacity } = options;

	// Compensate for DPI difference: canvas (96 DPI) vs PDF (72 DPI)
	const scale = 1.33; // 96 / 72
	const canvasWidth = Math.round(width * scale);
	const canvasHeight = Math.round(height * scale);

	const canvas = document.createElement('canvas');
	canvas.width = canvasWidth;
	canvas.height = canvasHeight;
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('Could not get canvas context');

	// Dynamically calculate font size so the widest line is 50% of the PDF width (in points, scaled)
	const lines = text.split('\n');
	const fontFamily = 'Helvetica';
	let fontSize = 300 * scale; // Start with a large font size, scaled

	// Create a temporary canvas context for measuring text
	const tempCanvas = document.createElement('canvas');
	const tempCtx = tempCanvas.getContext('2d');
	if (!tempCtx) throw new Error('Could not get temp canvas context');

	// Find the maximum font size that fits 50% of the PDF width (scaled)
	let maxLineWidth = 0;
	while (fontSize > 1) {
		tempCtx.font = `${fontSize}px ${fontFamily}`;
		maxLineWidth = Math.max(
			...lines.map((line) => tempCtx.measureText(line).width),
		);
		if (maxLineWidth <= canvasWidth * 0.5) break; // Fill 50% of the PDF width (scaled)
		fontSize--;
	}

	// Configure watermark text
	ctx.save();
	ctx.globalAlpha = opacity;
	ctx.font = `${fontSize}px ${fontFamily}`;
	ctx.fillStyle = color;
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.translate(canvasWidth / 2, canvasHeight / 2);
	ctx.rotate(-Math.PI / 4); // Rotate text by -45 degrees

	// Draw each line
	const lineHeight = fontSize * 1.5;
	const totalHeight = lineHeight * (lines.length - 1);
	const startY = -(totalHeight / 2);
	lines.forEach((line, index) => {
		ctx.fillText(line, 0, startY + index * lineHeight);
	});

	ctx.restore();

	// Convert canvas to blob
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (blob) resolve(blob);
				else reject(new Error('Failed to create blob from canvas'));
			},
			'image/png',
			1.0,
		);
	});
}

/**
 * Adds a watermark to a PDF document
 * @param pdfUrl The URL of the PDF to watermark
 * @param options Watermark options including text and styling
 * @returns A Promise that resolves to a Blob containing the watermarked PDF
 */
export async function addPdfWatermark(
	pdfUrl: string,
	options: WatermarkOptions,
): Promise<Blob> {
	const {
		text,
		opacity = DEFAULT_WATERMARK_OPTIONS.opacity!,
		color = DEFAULT_WATERMARK_OPTIONS.color!,
	} = options;

	// Fetch the PDF
	const pdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());

	// Load the PDF document
	const pdfDoc = await PDFDocument.load(pdfBytes);

	// Get all pages
	const pages = pdfDoc.getPages();

	// Add watermark to each page FIRST
	for (const page of pages) {
		const { width, height } = page.getSize();

		// Create watermark canvas at scaled size, with text filling 50% of width
		const watermarkBlob = await createWatermarkCanvas(text, {
			width,
			height,
			color,
			opacity,
		});

		// Convert blob to array buffer
		const watermarkArrayBuffer = await watermarkBlob.arrayBuffer();

		// Embed the watermark image
		const watermarkImage = await pdfDoc.embedPng(
			new Uint8Array(watermarkArrayBuffer),
		);

		// Draw the watermark image at full page size
		page.drawImage(watermarkImage, {
			x: 0,
			y: 0,
			width,
			height,
		});
	}

	// THEN resize each page to max 1600 width if needed (scaling watermark and content together)
	for (const page of pages) {
		const { width, height } = page.getSize();
		if (width > 1600) {
			const scale = 1600 / width;
			const newWidth = 1600;
			const newHeight = height * scale;
			page.setSize(newWidth, newHeight);
			page.scaleContent(scale, scale);
		}
	}

	// Save the modified PDF
	const modifiedPdfBytes = await pdfDoc.save();
	return new Blob([new Uint8Array(modifiedPdfBytes)], {
		type: 'application/pdf',
	});
}

/**
 * Adds a watermark to a file (image or PDF) based on its type
 * @param fileUrl The URL of the file to watermark
 * @param fileType The type of file ('pdf', 'jpeg', 'jpg', 'png')
 * @param options Optional watermark options (text, opacity, fontSize, color)
 * @returns A Promise that resolves to a Blob containing the watermarked file
 */
export async function addWatermark(
	fileUrl: string,
	fileType: 'pdf' | 'jpeg' | 'jpg' | 'png',
	options?: Partial<WatermarkOptions>,
): Promise<Blob> {
	const timestamp = new Date().toLocaleString();
	const defaultText = `Watermarked\n${timestamp}`;

	const watermarkOptions: WatermarkOptions = {
		...DEFAULT_WATERMARK_OPTIONS,
		text: options?.text || defaultText,
		opacity: options?.opacity,
		fontSize: options?.fontSize,
		color: options?.color,
	};

	if (fileType === 'pdf') {
		return addPdfWatermark(fileUrl, watermarkOptions);
	} else {
		return addImageWatermark(fileUrl, watermarkOptions);
	}
}
